
import { QueryClient, type Query } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { useState } from 'react';
import { StoreProvider } from '@/stores/StoreProvider';
import { createIdbPersister, shouldPersistQuery } from '@/lib/queryPersister';
import { isNetworkError } from '@/lib/api/errors';

// Bump when persisted cache shape becomes incompatible (e.g. queryKey schema change).
// Tenant isolation is handled by explicit purgePersistedCache() in AuthContext on
// switchOrg/logout, not by the buster.
const CACHE_BUSTER = 'v1';

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60_000,         // 5 min — SignalR invalidates on real changes
            gcTime: 24 * 60 * 60_000,      // 24h — long-lived in memory + persistence
            refetchOnWindowFocus: false,
            refetchOnReconnect: 'always',  // refresh after connectivity returns
            placeholderData: (prev: unknown) => prev,  // no flash while refetching
            // Don't burn retries on 502/503/504/fetch errors — the NetworkStatus
            // heartbeat + refetchOnReconnect will pick them up when the server is back.
            retry: (count, err) => !isNetworkError(err) && count < 2,
          },
        },
      })
  );

  const [persister] = useState(() => createIdbPersister());

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60_000,
        buster: CACHE_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: (query: Query) => shouldPersistQuery(query),
        },
      }}
    >
      <StoreProvider>
        {children}
      </StoreProvider>
    </PersistQueryClientProvider>
  );
}
